import React from 'react'
import {Button} from '@/components/cta'
import {Eyebrow} from '../ui/eyebrow'
import {useHasInnerText} from 'hec/hooks/useHasInnerText'
import {TextBlockProps} from './types'

const TextBlock = ({
    eyebrowType,
    eyebrowIcon,
    eyebrow,
    headlineType,
    headlineImage,
    headline,
    supportText,
    couponCode,
    enableCouponCode,
    eyebrowBackgroundColor,
    eyebrowTextColor,
    cta,
    buttonColor,
    overlay,
    eyebrowSize,
    disclaimer,
    link,
    alignment,
    overlayColor,
    textBlock,
    buttonTextColor
}: TextBlockProps) => {
    const hasHeadlineContent = useHasInnerText(headline ?? '')
    const hasSupportText = useHasInnerText(supportText ?? '')
    const hasTextBlock = useHasInnerText(textBlock ?? '')
    const hasDisclaimer = useHasInnerText(disclaimer ?? '')

    let overlayBgStyle: React.CSSProperties | undefined = undefined
    if (overlay) {
        overlayBgStyle = {
            backgroundColor: overlayColor || 'rgba(0,0,0,0.25)',
            opacity: 0.25,
            position: 'absolute',
            inset: 0,
            borderRadius: 24,
            zIndex: 0
        }
    }
    // Map alignment prop to Tailwind text alignment classes
    const alignmentClass =
        alignment === 'center' ? 'text-center' : alignment === 'right' ? 'text-right' : 'text-left'
    // Map alignment prop to Tailwind justify classes for container
    const containerJustifyClass =
        alignment === 'center'
            ? 'items-center'
            : alignment === 'right'
            ? 'items-end'
            : 'items-start'
    return (
        <div className="text-block-container flex flex-col gap-[20px] max-w-[350px] md:max-w-[400px] lg:max-w-[600px] lg:min-w-[400px] justify-center">
            <div className="text-block-content flex flex-col  gap-[16px] self-stretch rounded-[24px] p-[40px] relative overflow-hidden">
                {overlay && <div style={overlayBgStyle} />}
                <div
                    className={`relative z-8 w-full flex flex-col gap-[16px] ${containerJustifyClass}`}
                >
                    <div className="flex flex-col gap-2">
                        {eyebrowType === 'none' ? null : eyebrow && eyebrowType !== 'icon' ? (
                            <Eyebrow
                                size={eyebrowSize}
                                tag="div"
                                data-testid="eyebrow"
                                type={eyebrowType}
                                eyebrowBackgroundColor={eyebrowBackgroundColor}
                                textColor={eyebrowTextColor}
                                dangerouslySetInnerHTML={{__html: eyebrow}}
                            ></Eyebrow>
                        ) : (
                            <img
                                src={eyebrowIcon}
                                alt="Eyebrow Icon"
                                className="inline-block mr-2 w-[48px] h-[48px]"
                            />
                        )}
                    </div>
                    {hasHeadlineContent && headlineType == 'text' && headline ? (
                        <div className="text-block-content__headline flex  gap-[77px]">
                            <p
                                dangerouslySetInnerHTML={{__html: headline}}
                                className={`font-grotesque text-[30px] md:text-[40px] lg:text-[48px] not-italic font-normal leading-[110%] tracking-[-0.48px] ${alignmentClass}`}
                            ></p>
                        </div>
                    ) : headlineType === 'image' && headlineImage ? (
                        <img
                            src={headlineImage}
                            alt="Headline"
                            className="inline-block  w-auto h-auto max-w-[250px] lg:max-w-[300px]"
                        />
                    ) : null}
                    {hasSupportText && supportText && (
                        <div className="text-block-content__support-text flex  gap-[77px] ">
                            <p
                                dangerouslySetInnerHTML={{__html: supportText}}
                                className={`font-grotesque body-lg lg:body-xl not-italic font-normal leading-[150%] tracking-[0] ${alignmentClass}`}
                            ></p>
                        </div>
                    )}
                    {hasTextBlock && textBlock && (
                        <div className="text-block-content__support-text flex  gap-[77px] self-stretch">
                            <p
                                dangerouslySetInnerHTML={{__html: textBlock}}
                                className={`font-grotesque body-lg lg:body-xl not-italic font-normal leading-[150%] tracking-[0] ${alignmentClass}`}
                            ></p>
                        </div>
                    )}
                    {enableCouponCode && couponCode && (
                        <div
                            className={`text-block-content__coupon-code body-lg ${alignmentClass}`}
                        >
                            Coupon Code: <span>CODE-{couponCode}</span>
                        </div>
                    )}
                    <div
                        className={`text-block-content__cta flex ${
                            alignment === 'center'
                                ? 'justify-center'
                                : alignment === 'right'
                                ? 'justify-end'
                                : 'justify-start'
                        }`}
                    >
                        <Button
                            clickFunction="link"
                            variant="primary"
                            ctaType="button"
                            url={link}
                            style={{backgroundColor: buttonColor, color: buttonTextColor}}
                        >
                            {cta}
                        </Button>
                    </div>
                    {hasDisclaimer && disclaimer && (
                        <div
                            dangerouslySetInnerHTML={{__html: disclaimer}}
                            className={`text-block-content__disclaimer text-black font-grotesque  not-italic font-light leading-tight tracking-[0] mb-6  ${alignmentClass}`}
                        ></div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default TextBlock
